import { useRef, useState } from 'react';
import { UploadCloud, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const DEFAULT_ACCEPT = '.pdf,.docx,.pptx,.xlsx,.md,.markdown,.txt';
const DEFAULT_ACCEPTED_RE = /\.(pdf|docx|md|markdown|txt)$/i;

interface FileDropzoneProps {
  onFilesAdded: (files: File[]) => void;
  accept?: string;
  acceptedRegex?: RegExp;
  multiple?: boolean;
  disabled?: boolean;
  disabledHint?: string;
  label?: string;
  helperText?: string;
  className?: string;
  /**
   * Extra classes for the dashed drop area itself, not the wrapper `className` sits on.
   *
   * Separate because the two are different things and only one of them is the target: a caller that
   * wants a tall drop zone has to grow the *inner* box, and putting a height on the wrapper instead
   * leaves a small dashed rectangle floating in a tall empty one.
   */
  zoneClassName?: string;
}

// Shared drag-and-drop + browse-button file picker, extracted from Ingest.tsx so the
// same upload UX can be reused in the "create project from files" flow. Only handles
// selecting/filtering files by extension — what happens to the accepted files is up
// to the caller via onFilesAdded.
export function FileDropzone({
  onFilesAdded,
  accept = DEFAULT_ACCEPT,
  acceptedRegex = DEFAULT_ACCEPTED_RE,
  multiple = true,
  disabled = false,
  disabledHint,
  label,
  helperText,
  className,
  zoneClassName,
}: FileDropzoneProps) {
  const [dragActive, setDragActive] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: File[]) => {
    const valid = files.filter((f) => acceptedRegex.test(f.name));
    // Dropping an unsupported file used to do nothing at all, which reads as a broken
    // dropzone — name what was skipped instead (.doc especially, since .docx is accepted).
    setRejected(files.filter((f) => !acceptedRegex.test(f.name)).map((f) => f.name));
    if (valid.length > 0) onFilesAdded(valid);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (disabled) return;
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    handleFiles(files);
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleFileInputChange}
      />

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-3 text-center transition-colors py-8',
          dragActive ? 'border-orange-400 bg-orange-50/80 dark:bg-orange-950/30' : 'border-border bg-surface-subtle',
          zoneClassName,
        )}
      >
        <div className={cn(
          'w-12 h-12 rounded-full flex items-center justify-center',
          dragActive ? 'bg-orange-100 dark:bg-orange-950/50' : 'bg-card border border-border',
        )}>
          <UploadCloud size={20} className={dragActive ? 'text-orange-500' : 'text-ink-faint'} />
        </div>
        <div>
          <p className="text-sm font-medium text-ink-secondary">
            {dragActive ? 'Drop to upload' : (label ?? 'Drag & drop .pdf, .docx, .pptx, .xlsx or .md files here')}
          </p>
          {helperText && <p className="text-xs text-ink-faint mt-1">{helperText}</p>}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
        >
          <FileText size={13} />
          Browse files
        </Button>
        {disabled && disabledHint && (
          <p className="text-[11px] text-ink-faint">{disabledHint}</p>
        )}
        {rejected.length > 0 && (
          <p className="text-[11px] text-danger-text px-6">
            Skipped {rejected.join(', ')} — supported types are {accept.replace(/,/g, ', ')}
            {rejected.some((n) => /\.doc$/i.test(n)) && ' (re-save older .doc files as .docx)'}
          </p>
        )}
      </div>
    </div>
  );
}
